import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Users, 
  UserCheck, 
  UserX, 
  Shield, 
  Trash2, 
  Search,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  AlertTriangle
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

const ROLE_LABELS = {
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200' },
  manager: { label: 'Manager', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200' },
  lead: { label: 'Lead', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200' },
  senior_artist: { label: 'Senior Artist', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200' },
  artist: { label: 'Artist', color: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' },
  viewer: { label: 'Viewer', color: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200' }
};

const UserManagement = () => {
  const { isDark } = useTheme();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/admin/roles');
      setAvailableRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'role' }));
    try {
      await api.put(`/admin/users/${userId}/role?role=${newRole}`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (error) {
      console.error('Error updating role:', error);
      alert(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleApprove = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'approve' }));
    try {
      await api.put(`/admin/users/${userId}/approve`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_approved: true } : u));
    } catch (error) {
      console.error('Error approving user:', error);
      alert(error.response?.data?.detail || 'Failed to approve user');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleActivate = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'activate' }));
    try {
      await api.put(`/admin/users/${userId}/activate`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: true } : u));
    } catch (error) {
      console.error('Error activating user:', error);
      alert(error.response?.data?.detail || 'Failed to activate user');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleDeactivate = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'deactivate' }));
    try {
      await api.put(`/admin/users/${userId}/deactivate`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: false } : u));
    } catch (error) {
      console.error('Error deactivating user:', error);
      alert(error.response?.data?.detail || 'Failed to deactivate user');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleDelete = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'delete' }));
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || 
                          (filterStatus === 'active' && user.is_active) ||
                          (filterStatus === 'inactive' && !user.is_active) ||
                          (filterStatus === 'pending' && !user.is_approved);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active).length,
    pending: users.filter(u => !u.is_approved).length,
    inactive: users.filter(u => !u.is_active).length
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className={cn("p-8 text-center", isDark ? "bg-slate-800 border-slate-700" : "")}>
          <Shield className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className={cn("text-xl font-bold mb-2", isDark ? "text-white" : "text-slate-800")}>
            Access Denied
          </h2>
          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
            Only administrators can access user management.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="user-management-page">
      {/* Header */}
      <div>
        <h1 className={cn(
          "text-2xl font-bold font-outfit flex items-center gap-2",
          isDark ? "text-white" : "text-slate-800"
        )}>
          <Users className="w-6 h-6 text-[#331062]" />
          User Management
        </h1>
        <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
          Manage user accounts, roles, and access permissions
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>{stats.total}</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Total Users</p>
            </div>
          </div>
        </Card>
        <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/50">
              <UserCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>{stats.active}</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Active</p>
            </div>
          </div>
        </Card>
        <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/50">
              <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>{stats.pending}</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Pending</p>
            </div>
          </div>
        </Card>
        <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/50">
              <UserX className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>{stats.inactive}</p>
              <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Inactive</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className={cn("p-4", isDark ? "bg-slate-800 border-slate-700" : "")}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-users-input"
            />
          </div>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-role-select">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {availableRoles.map(role => (
                <SelectItem key={role} value={role}>
                  {ROLE_LABELS[role]?.label || role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-status-select">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="pending">Pending Approval</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Users Table */}
      <Card className={cn("overflow-hidden", isDark ? "bg-slate-800 border-slate-700" : "")}>
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full mx-auto"></div>
            <p className={cn("mt-4 text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-400" />
            <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={cn("border-b", isDark ? "bg-slate-900/50 border-slate-700" : "bg-slate-50 border-slate-200")}>
                <tr>
                  <th className={cn("px-4 py-3 text-left text-xs font-semibold", isDark ? "text-slate-300" : "text-slate-600")}>User</th>
                  <th className={cn("px-4 py-3 text-left text-xs font-semibold", isDark ? "text-slate-300" : "text-slate-600")}>Role</th>
                  <th className={cn("px-4 py-3 text-center text-xs font-semibold", isDark ? "text-slate-300" : "text-slate-600")}>Status</th>
                  <th className={cn("px-4 py-3 text-center text-xs font-semibold", isDark ? "text-slate-300" : "text-slate-600")}>Approved</th>
                  <th className={cn("px-4 py-3 text-left text-xs font-semibold", isDark ? "text-slate-300" : "text-slate-600")}>Joined</th>
                  <th className={cn("px-4 py-3 text-center text-xs font-semibold", isDark ? "text-slate-300" : "text-slate-600")}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                <AnimatePresence>
                  {filteredUsers.map((user) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className={cn(
                        "transition-colors",
                        isDark ? "hover:bg-slate-700/50" : "hover:bg-slate-50",
                        user.id === currentUser?.id && (isDark ? "bg-slate-700/30" : "bg-blue-50/50")
                      )}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className={cn("font-medium text-sm", isDark ? "text-white" : "text-slate-800")}>
                            {user.name}
                            {user.id === currentUser?.id && (
                              <span className="ml-2 text-xs text-[#331062]">(You)</span>
                            )}
                          </p>
                          <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>{user.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {user.id === currentUser?.id ? (
                          <span className={cn("px-2 py-1 rounded-full text-xs font-medium", ROLE_LABELS[user.role]?.color)}>
                            {ROLE_LABELS[user.role]?.label || user.role}
                          </span>
                        ) : (
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleRoleChange(user.id, value)}
                            disabled={actionLoading[user.id] === 'role'}
                          >
                            <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`role-select-${user.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {availableRoles.map(role => (
                                <SelectItem key={role} value={role}>
                                  {ROLE_LABELS[role]?.label || role}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {user.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200">
                            <CheckCircle className="w-3 h-3" /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200">
                            <XCircle className="w-3 h-3" /> Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {user.is_approved ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApprove(user.id)}
                            disabled={actionLoading[user.id] === 'approve'}
                            className="h-7 text-xs"
                            data-testid={`approve-btn-${user.id}`}
                          >
                            {actionLoading[user.id] === 'approve' ? 'Approving...' : 'Approve'}
                          </Button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {user.id !== currentUser?.id && (
                          <div className="flex items-center justify-center gap-2">
                            {user.is_active ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeactivate(user.id)}
                                disabled={actionLoading[user.id] === 'deactivate'}
                                className="h-7 text-xs text-yellow-600 border-yellow-300 hover:bg-yellow-50 dark:text-yellow-400 dark:border-yellow-800 dark:hover:bg-yellow-900/30"
                                data-testid={`deactivate-btn-${user.id}`}
                              >
                                <UserX className="w-3 h-3 mr-1" />
                                Deactivate
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleActivate(user.id)}
                                disabled={actionLoading[user.id] === 'activate'}
                                className="h-7 text-xs text-green-600 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-800 dark:hover:bg-green-900/30"
                                data-testid={`activate-btn-${user.id}`}
                              >
                                <UserCheck className="w-3 h-3 mr-1" />
                                Activate
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteConfirm(user.id)}
                              className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/30"
                              data-testid={`delete-btn-${user.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className={cn("p-6 rounded-lg max-w-md w-full mx-4", isDark ? "bg-slate-800" : "bg-white")}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/50">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className={cn("text-lg font-semibold", isDark ? "text-white" : "text-slate-800")}>
                  Delete User
                </h3>
              </div>
              <p className={cn("text-sm mb-6", isDark ? "text-slate-300" : "text-slate-600")}>
                Are you sure you want to delete this user? This action cannot be undone and will also remove their associated designer profile.
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                  data-testid="cancel-delete-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={actionLoading[deleteConfirm] === 'delete'}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-btn"
                >
                  {actionLoading[deleteConfirm] === 'delete' ? 'Deleting...' : 'Delete User'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserManagement;
